import api from '@/lib/axios';

export interface ClientContractListItem {
  contract: ClientContract;
  client: {
    id: string;
    name: string;
    tradeName: string | null;
    isActive?: boolean;
  };
}

export interface ClientContract {
  id: string;
  tenantId: string;
  clientId: string;
  contractType: string;
  parentContractId?: string | null;
  startDate: string;
  endDate: string;
  noticePeriodDays: number;
  renewalType: string;
  paymentFrequency: string;
  paymentDueDay: number;
  billingType: string;
  monthlyValue: string;
  setupValue: string;
  currency: string;
  adjustmentIndex?: string | null;
  adjustmentBaseDate?: string | null;
  adjustedAt?: string | null;
  nextAdjustmentDate?: string | null;
  lastAdjustmentDate?: string | null;
  lastAdjustmentPercentage?: string | null;
  filePath?: string | null;
  storageKey?: string | null;
  scope?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ClientContractDetail {
  contract: ClientContract;
  client: {
    id: string;
    name: string;
    tradeName: string | null;
    cnpj: string | null;
  };
}

export interface DashboardStats {
  total: number;
  expiringIn90Days: number;
  expired: number;
}

export interface ClientContractHistoryItem {
  id: string;
  tenantId: string;
  entityId: string;
  action: 'create' | 'update' | 'delete';
  changes: Record<string, unknown> | null;
  createdAt: string;
  changedBy: string | null;
  changedByUser?: {
    id: string;
    email: string;
  } | null;
}

export interface ContractNotificationSettings {
  notifyTeamId: string | null;
  daysBeforeExpiration: number;
  notifyByEmail: boolean;
}

export interface CreateClientContractDto {
  clientId: string;
  contractType?: 'contract' | 'addendum';
  parentContractId?: string;
  startDate: string;
  endDate: string;
  noticePeriodDays?: number;
  renewalType?: 'manual' | 'automatic';
  paymentFrequency?: 'monthly';
  paymentDueDay?: number;
  billingType?: 'monthly' | 'two_installments';
  monthlyValue: number;
  setupValue?: number;
  currency?: string;
  adjustmentIndex?: string | null;
  adjustmentBaseDate?: string | null;
  adjustedAt?: string | null;
  nextAdjustmentDate?: string | null;
  scope?: string | null;
}

export interface UpdateClientContractDto {
  contractType?: 'contract' | 'addendum';
  parentContractId?: string | null;
  startDate?: string;
  endDate?: string;
  noticePeriodDays?: number;
  renewalType?: 'manual' | 'automatic';
  paymentFrequency?: 'monthly';
  paymentDueDay?: number;
  billingType?: 'monthly' | 'two_installments';
  monthlyValue?: number;
  setupValue?: number;
  currency?: string;
  adjustmentIndex?: string | null;
  adjustmentBaseDate?: string | null;
  adjustedAt?: string | null;
  nextAdjustmentDate?: string | null;
  lastAdjustmentDate?: string | null;
  lastAdjustmentPercentage?: string | null;
  scope?: string | null;
}

export interface ClientContractFilters {
  clientId?: string;
  startDate?: string;
  endDate?: string;
  clientStatus?: 'active' | 'inactive' | 'all';
}

export const clientContractsService = {
  async list(filters?: ClientContractFilters): Promise<ClientContractListItem[]> {
    const params = new URLSearchParams();
    if (filters?.clientId) params.append('clientId', filters.clientId);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.clientStatus) params.append('clientStatus', filters.clientStatus);
    const response = await api.get<ClientContractListItem[]>(
      `/client-contracts?${params.toString()}`,
    );
    return response.data;
  },

  async getById(id: string): Promise<ClientContractDetail> {
    const response = await api.get<ClientContractDetail>(`/client-contracts/${id}`);
    return response.data;
  },

  async getDashboard(): Promise<DashboardStats> {
    const response = await api.get<DashboardStats>('/client-contracts/dashboard');
    return response.data;
  },

  async listHistory(contractId?: string): Promise<ClientContractHistoryItem[]> {
    const params = new URLSearchParams();
    if (contractId) params.append('contractId', contractId);
    const response = await api.get<ClientContractHistoryItem[]>(
      `/client-contracts/history?${params.toString()}`,
    );
    return response.data;
  },

  async getContractHistory(contractId: string): Promise<ClientContractHistoryItem[]> {
    const response = await api.get<ClientContractHistoryItem[]>(
      `/client-contracts/${contractId}/history`,
    );
    return response.data;
  },

  async getSettings(): Promise<ContractNotificationSettings> {
    const response = await api.get<ContractNotificationSettings>(
      '/client-contracts/settings',
    );
    return response.data;
  },

  async updateSettings(data: {
    notifyTeamId?: string | null;
    daysBeforeExpiration?: number;
    notifyByEmail?: boolean;
  }): Promise<ContractNotificationSettings> {
    const response = await api.patch<ContractNotificationSettings>(
      '/client-contracts/settings',
      data,
    );
    return response.data;
  },

  async create(
    data: CreateClientContractDto,
    file: File,
  ): Promise<ClientContract> {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        formData.append(key, String(value));
      }
    });
    const response = await api.post<ClientContract>(
      '/client-contracts',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
    return response.data;
  },

  async update(id: string, data: UpdateClientContractDto): Promise<ClientContract> {
    const response = await api.patch<ClientContract>(
      `/client-contracts/${id}`,
      data,
    );
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/client-contracts/${id}`);
  },

  async getDownloadUrl(id: string): Promise<{ url: string }> {
    const response = await api.get<{ url: string }>(
      `/client-contracts/${id}/download`,
    );
    return response.data;
  },
};
